import { FileInputField } from "@deere/fuel-react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const MIN_FILE_SIZE = 0;
const MAX_FILE_SIZE = 20480000;

const acceptFileTypes = {
  "text/csv": [".csv"],
};
const OrderFileUploadModal = ({
  openModal,
  closeModalCallback,
  importLinesButtonCallback,
}: {
  openModal: boolean;
  closeModalCallback: () => void;
  importLinesButtonCallback: (files: File[]) => void;
}) => {
  const { t } = useTranslation(["orders", "common"]);
  const [files, setFiles] = useState<File[]>([]);
  const theme = useTheme();

  function isFileSizeValid(file: File) {
    return file.size > MIN_FILE_SIZE && file.size < MAX_FILE_SIZE;
  }

  function HelperText() {
    if (files.length === 0 || (files[0] && isFileSizeValid(files[0]))) {
      return (
        <>
          {t("orders:fileUploadModal.helperText1")}{" "}
          <b>Comma Separated Value (.csv)</b>{" "}
          {t("orders:fileUploadModal.helperText2")} <b>20MB</b>
        </>
      );
    }
    return (
      <span style={{ color: theme.tokens.FuelColorAlertErrorIcon }}>
        {files[0]?.size === 0
          ? t("orders:fileUploadModal.blankFileSizeWarning")
          : t("orders:fileUploadModal.maximumFileSizeWarning")}
      </span>
    );
  }

  const downloadTemplate = () => {
    const headers = {
      Quantity: "Quantity",
      PartNumber: "PartNumber",
      Bin: "Bin",
    };

    const items = Array.from({ length: 10 }, () => ({
      Quantity: "",
      PartNumber: "",
      Bin: "",
    }));

    const rowData = [headers, ...items];
    const csv = rowData
      .map((data) => Object.values(data).join(","))
      .join("\r\n");

    const blob = new Blob([csv], { type: "text/csv" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Bulk line upload.csv";

    link.click();
  };

  function label() {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: theme.tokens.FuelSpacing2,
        }}
      >
        <Typography
          data-testid="upload-file-label"
          sx={{
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            fontSize: theme.tokens.FuelFontSizeHeading4,
          }}
        >
          {t("orderLines.uploadFile")}
        </Typography>

        <Button
          startIcon={<DownloadIcon data-testid="download-icon" />}
          onClick={downloadTemplate}
          variant="secondary"
          size="medium"
          data-testid="modal-download-template-button"
        >
          {t("orderLines.downLoadTemplate")}
        </Button>
      </div>
    );
  }

  const onCloseCallback = () => {
    setFiles([]);
    closeModalCallback();
  };

  return (
    <Dialog
      data-testid="order-file-upload-modal"
      fullWidth
      open={openModal}
      onClose={onCloseCallback}
      aria-labelledby="alert-Modal-title"
      aria-describedby="alert-Modal-description"
    >
      <DialogTitle data-testid="modal-title">
        {t("orders:orderLines.orderGrid.importLines")}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCloseCallback}
        size="large"
        sx={{
          position: "absolute",
          right: 15,
          top: 15,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <FileInputField
          id="order-file-input"
          maxSize={20480000}
          accept={acceptFileTypes}
          maxFiles={1}
          textMap={{
            actionAdd: t("common:modal.add.file") || "Add File(s)",
            actionChoose: t("common:modal.choose.file") || "Choose File(s)",
            actionClear: t("common:modal.clear.file") || "Clear File(s)",
            actionSupplement:
              t("common:modal.supplement") || "or drag here to upload",
          }}
          name="file-input-example"
          label={label()}
          value={files}
          files={files}
          hideValidationsPanel
          helperText={HelperText()}
          sx={{
            "& .FileInputField-root": {
              outline:
                files.length === 0 ||
                (files[0] && files[0].size < MAX_FILE_SIZE)
                  ? "dashed 0.125rem #cccccc"
                  : "dashed 0.125rem #df5353",
            },
            "& .MuiFormHelperText-root": {
              display: "flex",
              alignItems: "center",
              alignContent: "center",
              marginTop: theme.tokens.FuelSpacing1,
              gap: theme.tokens.FuelSpacing1,
            },
          }}
          onRemoveFiles={() => new Promise(() => {})}
          validators={[
            {
              key: "File size validation",
              displayText: "File size",
              validator: (file, resolve) => {
                resolve(isFileSizeValid(file));
              },
            },
          ]}
          onDropAccepted={(acceptedFiles) => {
            setFiles(acceptedFiles);
          }}
          onClear={() => {
            setFiles([]);
          }}
        />
      </DialogContent>
      <Divider
        sx={{
          borderWidth: "1px",
          borderColor: `${theme.tokens.FuelColorBorderLight}!important`,
        }}
        orientation="vertical"
      />
      <DialogActions>
        <Button
          onClick={() => {
            setFiles([]);
            closeModalCallback();
          }}
          variant="secondary"
          data-testid="modal-secondary-cancel-button"
        >
          {t("common:modal.cancelBtn")}
        </Button>
        <Button
          disabled={
            files.length === 0 ||
            files[0].size === 0 ||
            files[0].size > MAX_FILE_SIZE
          }
          onClick={() => {
            importLinesButtonCallback(files);
            setFiles([]);
          }}
          variant="primary"
          data-testid="modal-primary-import-lines-button"
        >
          {t("orders:orderLines.orderGrid.importLines")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderFileUploadModal;
